import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * VendorAuthContext - Vendor phone-OTP session management.
 *
 * Coexists alongside organizer AuthContext (different ss_vendor cookie).
 * On mount: GET /vendor/me to rehydrate from 30-day sliding session cookie.
 * Exposes:
 *   vendor          - Vendor | null
 *   loading         - bool (initial /me fetch pending)
 *   requestOtp      - (phone) => Promise<{ demoCode? }>
 *   verifyOtp       - (phone, code) => Promise<void>
 *   logout          - () => Promise<void>
 *   requireAuth     - () => Promise<void>  imperative gate: resolves once vendor
 *                     session exists; opens the modal and awaits success if not.
 *   patchProfile    - (name?, email?) => Promise<void>
 *
 * Auth modal selection:
 *   - VITE_MSG91_WIDGET_ID is set → MSG91WidgetModal (production path).
 *     The widget handles phone + OTP; on success we POST the JWT to
 *     /vendor/auth/verify-widget-token to get our session cookie.
 *   - VITE_MSG91_WIDGET_ID is NOT set → VendorAuthModal fallback (demo OTP,
 *     hits /vendor/auth/request-otp + verify-otp). Keeps local dev working
 *     without MSG91 credentials.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { api, AppError } from "@/lib/api";
import { VendorAuthModal } from "./VendorAuthModal";
import { MSG91WidgetModal } from "./MSG91WidgetModal";
import { useMSG91Script } from "./useMSG91Script";
import { VendorModalCallbacks } from "./vendorModalCallbacks";
// ── Context ──────────────────────────────────────────────────────────────────
const VendorAuthContext = createContext(null);
// ── Provider ─────────────────────────────────────────────────────────────────
export function VendorAuthProvider({ children }) {
    const [state, setState] = useState({
        vendor: null,
        loading: true,
    });
    const [modalOpen, setModalOpen] = useState(false);
    // Determine which auth path to use at runtime.
    // Reading the env var here (not in a child component) so TypeScript can see
    // it's a string | undefined — the modal selection below narrows the type.
    const msg91WidgetId = import.meta.env.VITE_MSG91_WIDGET_ID;
    const useWidget = Boolean(msg91WidgetId);
    // Pre-load the MSG91 script so it's ready before the modal opens.
    // useMSG91Script is a no-op when VITE_MSG91_WIDGET_ID is not set.
    const { ready: widgetScriptReady } = useMSG91Script();
    // Queue of pending requireAuth() resolvers/rejectors.
    const pendingRef = useRef([]);
    // Rehydrate from cookie on mount.
    useEffect(() => {
        api
            .get("/vendor/me")
            .then(({ data }) => {
            setState({ vendor: data.vendor, loading: false });
        })
            .catch((err) => {
            const status = err instanceof AppError ? err.status : 0;
            if (status !== 401) {
                console.warn("[VendorAuthContext] /vendor/me error", err);
            }
            setState({ vendor: null, loading: false });
        });
    }, []);
    const requestOtp = useCallback(async (phone) => {
        const res = await api.post("/vendor/auth/request-otp", { phone });
        return { demoCode: res.demoCode };
    }, []);
    const verifyOtp = useCallback(async (phone, code) => {
        const res = await api.post("/vendor/auth/verify-otp", { phone, code });
        setState({ vendor: res.data.vendor, loading: false });
    }, []);
    const logout = useCallback(async () => {
        try {
            await api.post("/vendor/auth/logout", {});
        }
        catch {
            // Best-effort: clear client state regardless.
        }
        setState({ vendor: null, loading: false });
    }, []);
    const patchProfile = useCallback(async (updates) => {
        const res = await api.patch("/vendor/me", updates);
        setState((prev) => ({ ...prev, vendor: res.data.vendor }));
    }, []);
    // Called by VendorAuthModal on successful OTP verify + optional name step.
    const onLoginSuccess = useCallback((vendor) => {
        setState({ vendor, loading: false });
        setModalOpen(false);
        const pending = pendingRef.current.splice(0);
        pending.forEach(({ resolve }) => resolve());
    }, []);
    // Called when modal is dismissed without completing auth.
    const onModalDismissed = useCallback(() => {
        setModalOpen(false);
        const pending = pendingRef.current.splice(0);
        pending.forEach(({ reject }) => reject(new Error("Auth dismissed")));
    }, []);
    // requireAuth(): resolves immediately if vendor exists, else opens modal.
    const requireAuth = useCallback(() => {
        return new Promise((resolve, reject) => {
            // Use setState callback to read current state without stale closure.
            setState((prev) => {
                if (prev.vendor) {
                    resolve();
                    return prev;
                }
                pendingRef.current.push({ resolve, reject });
                setModalOpen(true);
                return prev;
            });
        });
    }, []);
    // Bridge callbacks value passed to VendorModalCallbacks context
    // so VendorAuthModal can notify us of success/dismiss without importing
    // VendorAuthContext (which would create a circular dep).
    const modalCallbacks = useMemo(() => ({ _onLoginSuccess: onLoginSuccess }), [onLoginSuccess]);
    return (_jsx(VendorAuthContext.Provider, { value: {
            ...state,
            requestOtp,
            verifyOtp,
            logout,
            requireAuth,
            patchProfile,
        }, children: _jsxs(VendorModalCallbacks.Provider, { value: modalCallbacks, children: [children, useWidget ? (_jsx(MSG91WidgetModal, { open: modalOpen, onOpenChange: (open) => {
                        if (!open)
                            onModalDismissed();
                    }, scriptReady: widgetScriptReady })) : (_jsx(VendorAuthModal, { open: modalOpen, onOpenChange: (open) => {
                        if (!open)
                            onModalDismissed();
                    } }))] }) }));
}
// ── Hook ─────────────────────────────────────────────────────────────────────
export function useVendorAuth() {
    const ctx = useContext(VendorAuthContext);
    if (!ctx) {
        throw new Error("useVendorAuth must be used inside <VendorAuthProvider>");
    }
    return ctx;
}
